import { UNITS } from 'constants/all';

import { EDITOR_MODES, SCALE_MODES } from '../constants';
import { types } from './actions';

const MAX_PAST_LENGTH = 50;

const initialState = {
  activeEntityId: null,
  caliperToolSelections: [],
  currentMeasurement: null,
  displayScale: 1.0,
  displayUnits: UNITS.IN,
  edgeHighlight: [],
  editorMode: EDITOR_MODES.SET_SCALE,
  excludedEdgeKeys: {
    past: [],
    present: [],
    future: [],
  },
  forceView: null,
  hideExcluded: true,
  highlight: [],
  isFinalizingContour: false,
  isInitializing: true,
  isMeasurementToolActive: false,
  isSelectorToolActive: false,
  measurementComparison: null,
  metaData: {},
  mode: null,
  partFileUuid: null,
  partMasterVersion: null,
  partUuid: null,
  scale: 1.0,
  scaleMode: SCALE_MODES.MEASUREMENT,
  selectorCumulativeInfo: {},
  selectorToolSelections: [],
  shouldShowSuccessMessage: false,
  totalEdgeCount: 0,
  vectorizationStatus: null,
  viewerData: null,
};

const reducer = (state = initialState, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.CONTOUR_SELECTOR_RECEIVE_EXCLUDED_KEYS: {
      const oldKeys = state.excludedEdgeKeys;
      return {
        ...state,
        excludedEdgeKeys: {
          past:
            state.excludedEdgeKeys.past.length < MAX_PAST_LENGTH
              ? [...oldKeys.past, oldKeys.present]
              : [...oldKeys.past.slice(1), oldKeys.present],
          present: payload.edgeKeys,
          future: [],
        },
      };
    }
    case types.CONTOUR_SELECTOR_RECEIVE_HIDE_EXCLUDED:
      return {
        ...state,
        hideExcluded: payload.hideExcluded,
      };
    case types.CONTOUR_SELECTOR_REDO: {
      const { past, present, future } = state.excludedEdgeKeys;
      return {
        ...state,
        excludedEdgeKeys: {
          past: [...past, present],
          present: future[0],
          future: future.slice(1),
        },
      };
    }
    case types.CONTOUR_SELECTOR_UNDO: {
      const { past, present, future } = state.excludedEdgeKeys;
      return {
        ...state,
        excludedEdgeKeys: {
          past: past.slice(0, past.length - 1),
          present: past[past.length - 1],
          future: [present, ...future],
        },
      };
    }
    case types.INIT_VECTOR_EDITOR:
      return {
        ...state,
        ...payload,
        isInitializing: false,
      };
    case types.INIT_VECTOR_EDITOR_FROM_STATUS_CHANGE:
      return {
        ...state,
        ...payload,
        isInitializing: false,
      };
    case types.MEASUREMENT_TOOL_DISABLE:
      return {
        ...state,
        isMeasurementToolActive: false,
        currentMeasurement: null,
        caliperToolSelections: [],
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_ENABLE:
      // measurement tool and selector tool cannot both be active at the same time
      // clear measurement tool when enabling when previously disabled
      return {
        ...state,
        isMeasurementToolActive: true,
        isSelectorToolActive: false,
        caliperToolSelections: state.isMeasurementToolActive
          ? state.caliperToolSelections
          : [],
        currentMeasurement: state.isMeasurementToolActive
          ? state.currentMeasurement
          : null,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_COMPARISON:
      return {
        ...state,
        measurementComparison: payload.comparison,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT:
      return {
        ...state,
        currentMeasurement: payload.distance,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS:
      return {
        ...state,
        caliperToolSelections: payload.selections,
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_REMOVE_SELECTIONS:
      return {
        ...state,
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MAKE_DXF_REQUEST:
    case types.UPDATE_CONTOUR_REQUEST:
      return {
        ...state,
        isFinalizingContour: true,
      };
    case types.MAKE_DXF_SUCCESS:
    case types.UPDATE_CONTOUR_SUCCESS:
      return {
        ...state,
        isFinalizingContour: false,
        shouldShowSuccessMessage: true,
      };
    case types.MAKE_DXF_ERROR:
    case types.UPDATE_CONTOUR_ERROR:
      return {
        ...state,
        isFinalizingContour: false,
      };
    case types.RECEIVE_DISPLAY_SCALE:
      return {
        ...state,
        displayScale: payload.displayScale,
      };
    case types.RECEIVE_DISPLAY_UNITS:
      return {
        ...state,
        displayUnits: payload.displayUnits,
      };
    case types.RECEIVE_EDGE_HIGHLIGHT:
      return {
        ...state,
        edgeHighlight: payload.edgeHighlight,
      };
    case types.RECEIVE_EDITOR_MODE:
      return {
        ...state,
        editorMode: payload.editorMode,
      };
    case types.RECEIVE_FORCE_VIEW:
      return {
        ...state,
        forceView: payload.forceView,
      };
    case types.RECEIVE_HIGHLIGHT:
      return {
        ...state,
        highlight: payload.highlight,
      };
    case types.RECEIVE_META_DATA:
      return {
        ...state,
        metaData: payload.metaData,
      };
    case types.RECEIVE_SCALE:
      return {
        ...state,
        scale: payload.scale,
      };
    case types.RECEIVE_SCALE_MODE:
      return {
        ...state,
        scaleMode: payload.scaleMode,
      };
    case types.RECEIVE_VECTORIZATION_STATUS:
      return {
        ...state,
        vectorizationStatus: payload.status,
      };
    case types.RECEIVE_VIEWER_DATA:
      return {
        ...state,
        viewerData: payload.viewerData,
      };
    case types.RESET:
      return {
        ...initialState,
      };
    case types.SELECTOR_TOOL_BOX_SELECT:
      return {
        ...state,
        selectorToolSelections: payload.selections,
      };
    case types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
      return {
        ...state,
        selectorToolSelections: [],
      };
    case types.SELECTOR_TOOL_DISABLE:
      return {
        ...state,
        isSelectorToolActive: false,
        selectorToolSelections: [],
        selectorCumulativeInfo: {},
      };
    case types.SELECTOR_TOOL_ENABLE: {
      // clear selections when enabling the selector tool
      const nextSelectorToolSelections = state.isSelectorToolActive
        ? state.selectorToolSelections
        : [];
      return {
        ...state,
        isMeasurementToolActive: false,
        isSelectorToolActive: true,
        //selectedObjects: [],
        selectorToolSelections: nextSelectorToolSelections,
        selectorCumulativeInfo: {},
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO:
      return {
        ...state,
        selectorCumulativeInfo: payload.cumulativeInfo,
      };
    case types.SELECTOR_TOOL_RECEIVE_SELECTION:
      return {
        ...state,
        selectorToolSelections: state.selectorToolSelections.concat([
          {
            ...payload.selection,
          },
        ]),
      };
    case types.SELECTOR_TOOL_REMOVE_SELECTION: {
      const isStep = payload.obj.includes('|');
      let selectorToolSelections = [];
      if (isStep) {
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.obj !== payload.obj,
        );
      } else {
        // stls
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.f !== payload.f,
        );
      }
      return {
        ...state,
        selectorToolSelections,
      };
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state) => state.modules.vectorFileEditor.app.page;

export const getActiveEntityId = (state) => getScope(state).activeEntityId;

export const getCaliperToolSelections = (state) =>
  getScope(state).caliperToolSelections;

export const getCurrentMeasurementValue = (state) =>
  getScope(state).currentMeasurement;

export const getDisplayScale = (state) => getScope(state).displayScale;

export const getDisplayUnits = (state) => getScope(state).displayUnits;

export const getEdgeHighlight = (state) => getScope(state).edgeHighlight;

export const getEditorMode = (state) => getScope(state).editorMode;

export const getPresentExcludedEdgeKeys = (state) =>
  getScope(state).excludedEdgeKeys.present;

export const getCanUndo = (state) =>
  getScope(state).excludedEdgeKeys.past.length > 0;

export const getCanRedo = (state) =>
  getScope(state).excludedEdgeKeys.future.length > 0;

export const getForceView = (state) => getScope(state).forceView;

export const getHideExcluded = (state) => getScope(state).hideExcluded;

export const getHighlight = (state) => getScope(state).highlight;

export const getIsFinalizingContour = (state) =>
  getScope(state).isFinalizingContour;

export const getIsMeasurementToolActive = (state) =>
  getScope(state).isMeasurementToolActive;

export const getIsInitializing = (state) => getScope(state).isInitializing;

export const getIsSelectorToolActive = (state) =>
  getScope(state).isSelectorToolActive;

export const getMeasurementComparison = (state) =>
  getScope(state).measurementComparison;

export const getMetaData = (state) => getScope(state).metaData;

export const getMode = (state) => getScope(state).mode;

export const getPartFileUuid = (state) => getScope(state).partFileUuid;

export const getScale = (state) => getScope(state).scale;

export const getScaleMode = (state) => getScope(state).scaleMode;

export const getSelectorCumulativeInfo = (state) =>
  getScope(state).selectorCumulativeInfo;

export const getSelectorToolSelections = (state) =>
  getScope(state).selectorToolSelections;

export const getShouldShowSuccessMessage = (state) =>
  getScope(state).shouldShowSuccessMessage;

export const getTotalEdgeCount = (state) => getScope(state).totalEdgeCount;

export const getVectorizationStatus = (state) =>
  getScope(state).vectorizationStatus;

export const getViewerData = (state) => getScope(state).viewerData;

export const getPartUuid = (state) => getScope(state).partUuid;

export const getPartMasterVersion = (state) =>
  getScope(state).partMasterVersion;
